/**
 * Premium "Partner Service Teaser" — der Ankündigungs-Beat VOR den
 * Service-/Produkt-Detailszenen.
 *
 * Bewusst minimalistisch: KEINE Item-Liste, KEIN Logo (das hat die
 * Card-Flip-Szene davor schon erledigt), KEINE URL/CTA. Nur die
 * vier marketingrelevanten Bausteine:
 *
 *   1. Eyebrow         — kontextueller Anker ("— ALLYVER PARTNER —")
 *   2. Partner-Name    — Letter-by-Letter-Reveal in Hero-Größe
 *   3. Stadt-Pill      — kleine Lokalisierung
 *   4. Zahl + Plural   — der eigentliche Hook („6 Dienstleistungen")
 *                        in einer Zeile, mit animiertem Akzent-Underline
 *
 * Singular/Plural: Bei `count === 1` setzt der Inspector-Loader automatisch
 * die Singularform („1 Dienstleistung" statt „1 Dienstleistungen"). Manche
 * Partner haben halt nur ein Angebot — das soll trotzdem stark wirken.
 *
 * Inputs:
 *   text       – Partner-Name (Hero-Headline)
 *   subtext    – Stadt / Ort (Pill)
 *   number     – Anzahl als String ("6", "1", "12"). Wird NICHT zweistellig
 *                gepaddet (1 bleibt 1).
 *   unit       – Bezeichnung im richtigen Numerus
 *                („Dienstleistungen" / „Dienstleistung" / „Produkte" …).
 *   accent     – Akzent (default primary).
 *   extras.sfx – Auto-SFX an/aus.
 *
 * `items`, `media`, `extras.lead`, `extras.tagline` werden NICHT mehr
 * gerendert — sie bleiben im Daten-Patch, falls eine spätere Variante sie
 * wieder einsetzt, aber die Szene zeigt sie nicht an.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  sceneSfxAudio, type SceneSfxBeat, typeSize, weights, withAlpha,
  colors as themeColors,
} from "../../reel/sceneShared.js";

export const partnerServiceTeaser: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = inputs.accent ? accentFor(inputs) : themeColors.primary;
  const isVertical = ctx.format === "9:16";

  const name = (inputs.text ?? "JUSATECH").trim();
  const city = (inputs.subtext ?? "").trim();

  // Zahl. Manche Partner haben nur 1 Angebot — wir zeigen die Zahl wie sie
  // ist (1, 6, 12) und überlassen die korrekte Singular/Plural-Wahl dem
  // `unit`-Feld (das der Inspector-Loader automatisch passend setzt).
  const rawNumber = (inputs.number ?? "0").toString().trim();
  const countLabel = /^\d+$/.test(rawNumber) ? rawNumber : "1";
  const unit = (inputs.unit ?? "Angebote").toString().trim();

  // ---- Typo-Sizes ---------------------------------------------------------
  const eyebrowSize = typeSize("xs", ctx.format) * 0.95;
  const nameSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.72;
  const citySize = typeSize("sm", ctx.format) * 0.78;
  const countSize = typeSize(isVertical ? "xl" : "hero", ctx.format) * 0.95;
  const unitSize = typeSize(isVertical ? "lg" : "md", ctx.format) * 0.85;

  // ---- IDs ----------------------------------------------------------------
  const t = makeTl(clipStartSec);
  const padId = `${clipPrefix}-pad`;
  const eyeId = `${clipPrefix}-eye`;
  const nameWrapId = `${clipPrefix}-namewrap`;
  const nameMaskId = `${clipPrefix}-namemask`;
  const cityId = `${clipPrefix}-city`;
  const lineTopId = `${clipPrefix}-line-top`;
  const lineBotId = `${clipPrefix}-line-bot`;
  const countWrapId = `${clipPrefix}-countwrap`;
  const countNumId = `${clipPrefix}-count-num`;
  const countUnitId = `${clipPrefix}-count-unit`;
  const unitUnderlineId = `${clipPrefix}-unit-underline`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  // ---- Name mit Mask-Reveal ----------------------------------------------
  // Wir wrappen den Namen in einen `overflow:hidden`-Container; ein
  // farbiger Overlay-Streifen wischt vertikal nach unten weg und gibt den
  // Namen frei. Sauberer Premium-Effekt ohne Letter-Splitting (Letter-
  // Splitting würde an dieser Stelle Zerfaserung erzeugen).
  const nameBlockHtml = `<div id="${nameWrapId}" style="position:relative;display:inline-block;padding:6px 14px;">
    <div style="position:relative;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize}px;line-height:0.95;letter-spacing:-2px;color:${themeColors.fg};text-align:center;">${escapeHtml(name)}</div>
    <div id="${nameMaskId}" style="position:absolute;inset:0;background:linear-gradient(180deg, ${withAlpha(accent, 0.0)} 0%, ${accent} 60%, ${withAlpha(accent, 0.0)} 100%);pointer-events:none;transform-origin:50% 0%;"></div>
  </div>`;

  // ---- City-Pill ----------------------------------------------------------
  const cityHtml = city
    ? `<div id="${cityId}" style="display:inline-flex;align-items:center;gap:8px;padding:6px 16px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.06)};border:1px solid ${withAlpha(themeColors.fg, 0.16)};font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${citySize}px;color:${withAlpha(themeColors.fg, 0.78)};">
        <svg width="${Math.round(citySize * 0.95)}" height="${Math.round(citySize * 0.95)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        <span>${escapeHtml(city)}</span>
      </div>`
    : "";

  // ---- Counter-Zeile („6 Dienstleistungen") ------------------------------
  // Eine Zeile, zwei Bausteine: große Akzent-Zahl + Plural-Wort mit
  // animiertem Underline-Sweep darunter. Visuell ruhig, marketingstark.
  const countHtml = `<div id="${countWrapId}" style="display:flex;flex-direction:row;align-items:baseline;justify-content:center;gap:${isVertical ? 18 : 22}px;flex-wrap:wrap;max-width:${isVertical ? 820 : 1080}px;">
    <div id="${countNumId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${countSize}px;line-height:1;letter-spacing:-3px;background:linear-gradient(180deg, ${themeColors.fg} 0%, ${themeColors.fg} 55%, ${accent} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;text-shadow:0 0 40px ${withAlpha(accent, 0.35)};">${escapeHtml(countLabel)}</div>
    <div style="position:relative;display:inline-block;">
      <div id="${countUnitId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${unitSize}px;line-height:1;letter-spacing:0.5px;color:${themeColors.fg};">${escapeHtml(unit)}</div>
      <div id="${unitUnderlineId}" style="position:absolute;left:0;right:0;bottom:-10px;height:3px;border-radius:9999px;background:linear-gradient(90deg, ${accent} 0%, ${withAlpha(accent, 0.6)} 100%);transform-origin:0% 50%;box-shadow:0 0 14px ${withAlpha(accent, 0.6)};"></div>
    </div>
  </div>`;

  // ---- Trennlinien um den Counter (subtile Marker) -----------------------
  const lineMaxW = isVertical ? 220 : 280;
  const lineHtml = (id: string) =>
    `<div id="${id}" style="width:${lineMaxW}px;max-width:60%;height:1px;background:linear-gradient(90deg, transparent 0%, ${withAlpha(themeColors.fg, 0.35)} 50%, transparent 100%);transform-origin:50% 50%;"></div>`;

  // ---- Choreografie -------------------------------------------------------
  //  0.10  Eyebrow fadet ein
  //  0.25  Name-Mask wischt nach unten weg (reveal)
  //  0.65  Stadt-Pill ein
  //  0.95  obere Linie zeichnet sich, gleich danach Counter
  //  1.05  Counter-Zahl poppt rein (scale)
  //  1.20  Plural-Wort fadet/slidet ein
  //  1.45  Underline unter Plural zieht von links nach rechts
  //  1.65  untere Linie ein
  const beatEye = 0.10;
  const beatName = 0.25;
  const beatCity = 0.65;
  const beatLineTop = 0.90;
  const beatNum = 1.05;
  const beatUnit = 1.22;
  const beatUnderline = 1.42;
  const beatLineBot = 1.65;

  // ---- Init ---------------------------------------------------------------
  const init = [
    gsapSetLine(`#${eyeId}`, { opacity: 0, y: -12 }),
    gsapSetLine(`#${nameMaskId}`, { scaleY: 1, opacity: 1 }),
    gsapSetLine(`#${nameWrapId}`, { opacity: 0 }),
    cityHtml ? gsapSetLine(`#${cityId}`, { opacity: 0, y: 10 }) : "",
    gsapSetLine(`#${lineTopId}`, { opacity: 0, scaleX: 0 }),
    gsapSetLine(`#${lineBotId}`, { opacity: 0, scaleX: 0 }),
    gsapSetLine(`#${countNumId}`, { opacity: 0, scale: 0.7, y: 14 }),
    gsapSetLine(`#${countUnitId}`, { opacity: 0, y: 18 }),
    gsapSetLine(`#${unitUnderlineId}`, { scaleX: 0, opacity: 0 }),
  ].filter(Boolean).join("\n");

  // ---- Tweens -------------------------------------------------------------
  const tweens: string[] = [
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.45, ease: 'raw:"power3.out"' }, beatEye),
    // Name: Wrapper sofort opacity 1, Maske wischt nach unten weg
    t.to(`#${nameWrapId}`, { opacity: 1, duration: 0.1, ease: 'raw:"none"' }, beatName),
    t.to(`#${nameMaskId}`, { scaleY: 0, transformOrigin: '50% 100%', duration: 0.7, ease: 'raw:"power3.inOut"' }, beatName),
  ];

  if (cityHtml) {
    tweens.push(t.to(`#${cityId}`, { opacity: 1, y: 0, duration: 0.5, ease: 'raw:"power3.out"' }, beatCity));
  }

  tweens.push(
    t.to(`#${lineTopId}`, { opacity: 1, scaleX: 1, duration: 0.55, ease: 'raw:"power3.out"' }, beatLineTop),
    t.to(`#${countNumId}`, { opacity: 1, scale: 1, y: 0, duration: 0.55, ease: 'raw:"back.out(1.6)"' }, beatNum),
    t.to(`#${countUnitId}`, { opacity: 1, y: 0, duration: 0.5, ease: 'raw:"power3.out"' }, beatUnit),
    t.to(`#${unitUnderlineId}`, { scaleX: 1, opacity: 1, duration: 0.55, ease: 'raw:"power3.out"' }, beatUnderline),
    t.to(`#${lineBotId}`, { opacity: 1, scaleX: 1, duration: 0.55, ease: 'raw:"power3.out"' }, beatLineBot),
  );

  // Sanftes „Atmen" der Akzent-Underline bis zum Outro
  const breathStart = beatUnderline + 0.6;
  const breathDur = Math.max(0.9, clipDurationSec - breathStart - 0.6);
  tweens.push(
    t.to(`#${unitUnderlineId}`, { opacity: 0.55, duration: breathDur / 2, repeat: -1, yoyo: true, ease: 'raw:"sine.inOut"' }, breathStart),
  );

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));

  // ---- SFX-Choreografie (dezent) -----------------------------------------
  const sfxBeats: SceneSfxBeat[] = [
    { sound: "tap", atSec: beatEye + 0.02, volume: 0.18, durSec: 0.4 },
    { sound: "swoosh", atSec: beatName + 0.05, volume: 0.20, durSec: 0.7 },
    { sound: "tick", atSec: beatLineTop + 0.05, volume: 0.10, durSec: 0.25 },
    { sound: "pop", atSec: beatNum + 0.05, volume: 0.22, durSec: 0.5 },
    { sound: "shimmer", atSec: beatUnderline + 0.02, volume: 0.18, durSec: 0.6 },
  ];
  const sfxHtml = sceneSfxAudio({ clipPrefix, clipStartSec, inputs, beats: sfxBeats });

  // ---- DOM ----------------------------------------------------------------
  const padTopBot = isVertical ? 90 : 70;
  const padX = isVertical ? 60 : 100;
  const html = `<div id="${padId}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 22 : 18}px;padding:${padTopBot}px ${padX}px;text-align:center;">
    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyebrowSize}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">— Allyver Partner —</div>
    ${nameBlockHtml}
    ${cityHtml}
    <div style="height:${isVertical ? 18 : 14}px;"></div>
    ${lineHtml(lineTopId)}
    <div style="height:${isVertical ? 8 : 6}px;"></div>
    ${countHtml}
    <div style="height:${isVertical ? 18 : 14}px;"></div>
    ${lineHtml(lineBotId)}
  </div>${sfxHtml ? `\n${sfxHtml}` : ""}`;

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
